import React, { useRef, useState, useEffect } from 'react';
import { roomsAPI } from '../services/meetingsAPI';
import { toast } from 'react-toastify';
import './AddMeetingRoomForm.css';

const AddMeetingRoomForm = ({ isOpen, onClose, onSubmit }) => {
  const TOTAL_STEPS = 5;
  const modalContentRef = useRef(null);

  const [formData, setFormData] = useState({
    roomName: '',
    roomType: 'Meeting Room',
    visibility: 'private',
    capacity: '',
    location: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    pricePerHour: '',
    amenities: [],
    availability: {
      monday: { available: true, startTime: '09:00', endTime: '18:00' },
      tuesday: { available: true, startTime: '09:00', endTime: '18:00' },
      wednesday: { available: true, startTime: '09:00', endTime: '18:00' },
      thursday: { available: true, startTime: '09:00', endTime: '18:00' },
      friday: { available: true, startTime: '09:00', endTime: '18:00' },
      saturday: { available: false, startTime: '09:00', endTime: '18:00' },
      sunday: { available: false, startTime: '09:00', endTime: '18:00' },
    },
    rules: '',
    description: '',
    services: '',
    surroundings: '',
    comments: '',
    policy: '',
    refundType: 'Fully',
    freeCancellationBefore: '',
    refundPercentage: '',
    ownerCancellationPenalty: '',
    autoFullRefundToUser: false,
    refundDescription: '',
    images: [],
  });

  const [currentStep, setCurrentStep] = useState(1);

  const amenitiesList = [
    'Wi-Fi',
    'Projector',
    'Whiteboard',
    'Video Conferencing',
    'Air Conditioning',
    'Parking',
    'Power Backup',
    'Coffee/Tea',
    'Printer/Scanner',
    'Sound System',
    'Natural Light',
    'Reception Service',
  ];

  const roomTypes = [
    'Meeting Room',
    'Conference Room',
    'Board Room',
    'Training Room',
    'Seminar Hall',
    'Office Cabin',
  ];

  useEffect(() => {
    if (isOpen && modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleAvailabilityChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          [field]: value
        }
      }
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    // Store as base64 so images persist in localStorage and show on Featured cards
    const readAsDataUrl = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    Promise.all(files.map(readAsDataUrl)).then((dataUrls) => {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...dataUrls]
      }));
    }).catch(() => {
      // Fallback to blob URLs if base64 fails (same session only)
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...files.map(f => URL.createObjectURL(f))]
      }));
    });
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only complete the process when user is on the final step


    if (currentStep !== TOTAL_STEPS) {
      return;
    }

    // Refund Policy validation
    if (!formData.refundType) {
      toast.error('Refund type is required.');
      return;
    }
    if ((formData.refundType === 'Fully' || formData.refundType === 'Partial') && (!formData.freeCancellationBefore || isNaN(Number(formData.freeCancellationBefore)))) {
      toast.error('Please enter free cancellation hours.');
      return;
    }
    if (formData.refundType === 'Partial' && (!formData.refundPercentage || isNaN(Number(formData.refundPercentage)))) {
      toast.error('Please enter refund percentage.');
      return;
    }
    if (formData.ownerCancellationPenalty && isNaN(Number(formData.ownerCancellationPenalty))) {
      toast.error('Owner cancellation penalty must be a number.');
      return;
    }

    // Frontend validation for required fields
    const trimmedName = String(formData.roomName || '').trim();
    if (!trimmedName) {
      toast.error('Room name is required.');
      return;
    }
    if (!formData.capacity || isNaN(Number(formData.capacity)) || Number(formData.capacity) < 1) {
      toast.error('Capacity is required and must be a positive number.');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Address is required.');
      return;
    }
    if (!formData.city.trim()) {
      toast.error('City is required.');
      return;
    }
    if (!formData.state.trim()) {
      toast.error('State is required.');
      return;
    }
    if (!formData.pincode.trim()) {
      toast.error('Pincode is required.');
      return;
    }

    // Prevent duplicate room name for this user (frontend check)
    const existingRooms = JSON.parse(localStorage.getItem('meetingRooms') || '[]');
    if (existingRooms.some(r => String(r.roomName || r.name).trim().toLowerCase() === trimmedName.toLowerCase())) {
      toast.error('You already have a meeting room with this name. Please use a different name.');
      return;
    }

    // Save to MongoDB via API
    try {
      const syncPayload = {
        name: trimmedName,
        description: String(formData.description || 'Meeting room').trim(),
        capacity: parseInt(formData.capacity, 10) || 10,
        amenities: formData.amenities || [],
        pricePerHour: parseFloat(formData.pricePerHour) || 0,
        city: String(formData.city || '').trim(),
        address: String(formData.address || '').trim(),
        state: String(formData.state || '').trim(),
        pincode: String(formData.pincode || '').trim(),
        location: String(formData.location || '').trim(),
        roomType: String(formData.roomType || 'Meeting Room').trim(),
        visibility: formData.visibility || 'private',
        images: formData.images || [],
        rules: String(formData.rules || '').trim(),
        services: String(formData.services || '').trim(),
        surroundings: String(formData.surroundings || '').trim(),
        comments: String(formData.comments || '').trim(),
        policy: String(formData.policy || '').trim(),
        availability: formData.availability || null,
        cleaningResponsibility: 'member_clean',
        cleaningCharge: 0,
        refundType: formData.refundType,
        freeCancellationBefore: formData.freeCancellationBefore,
        refundPercentage: formData.refundType === 'Partial' ? formData.refundPercentage : '',
        refundDescription: formData.refundDescription,
        ownerCancellationPenalty: formData.ownerCancellationPenalty,
        autoFullRefundToUser: formData.autoFullRefundToUser,
      };

      console.log('Submitting room payload:', syncPayload);
      const res = await roomsAPI.syncRoom(syncPayload);
      const savedRoom = res.data;

      const meetingRoomData = {
        ...formData,
        id: savedRoom._id,
        _id: savedRoom._id,
        createdAt: savedRoom.createdAt || new Date().toISOString(),
        hostId: JSON.parse(localStorage.getItem('user'))?._id,
      };

      // Also save to localStorage for backward compatibility
      const existingRooms = JSON.parse(localStorage.getItem('meetingRooms') || '[]');
      const updatedRooms = [meetingRoomData, ...existingRooms];
      localStorage.setItem('meetingRooms', JSON.stringify(updatedRooms));

      window.dispatchEvent(new Event('meetingRoomsUpdated'));
      toast.success('Meeting room added successfully!');
      onSubmit(meetingRoomData);
    } catch (err) {
      console.error('Error saving meeting room:', err);
      console.error('Backend error status:', err?.response?.status);
      console.error('Backend error body:', err?.response?.data);

      const backendMessage = err?.response?.data?.message;
      if (err?.response?.status && err.response.status < 500) {
        toast.error(backendMessage || 'Invalid meeting room data. Please check the form and try again.');
        return;
      }

      // Fallback: save to localStorage only
      const meetingRoomData = {
        ...formData,
        id: Date.now(),
        createdAt: new Date().toISOString(),
        hostId: JSON.parse(localStorage.getItem('user'))?._id,
      };
      const existingRooms = JSON.parse(localStorage.getItem('meetingRooms') || '[]');
      const updatedRooms = [meetingRoomData, ...existingRooms];
      localStorage.setItem('meetingRooms', JSON.stringify(updatedRooms));
      window.dispatchEvent(new Event('meetingRoomsUpdated'));
      toast.warn('Room saved locally. It may not be visible to other users.');
      onSubmit(meetingRoomData);
    }
    
    // Reset form
    setFormData({
      roomName: '',
      roomType: 'Meeting Room',
      visibility: 'private',
      capacity: '',
      location: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      pricePerHour: '',
      amenities: [],
      availability: {
        monday: { available: true, startTime: '09:00', endTime: '18:00' },
        tuesday: { available: true, startTime: '09:00', endTime: '18:00' },
        wednesday: { available: true, startTime: '09:00', endTime: '18:00' },
        thursday: { available: true, startTime: '09:00', endTime: '18:00' },
        friday: { available: true, startTime: '09:00', endTime: '18:00' },
        saturday: { available: false, startTime: '09:00', endTime: '18:00' },
        sunday: { available: false, startTime: '09:00', endTime: '18:00' },
      },
      rules: '',
      description: '',
      services: '',
      surroundings: '',
      comments: '',
      policy: '',
      images: [],
      refundType: 'Fully',
      freeCancellationBefore: '',
      refundPercentage: '',
      ownerCancellationPenalty: '',
      autoFullRefundToUser: false,
      refundDescription: '',
    });
    setCurrentStep(1);
    onClose();
  };

  const nextStep = () => setCurrentStep(prev => (prev >= TOTAL_STEPS ? TOTAL_STEPS : prev + 1));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  return (
    <div className="meeting-room-modal-overlay" onClick={onClose}>
      <div className="meeting-room-modal-content" onClick={(e) => e.stopPropagation()} ref={modalContentRef}>
        <div className="modal-header">
          <h2 className="modal-title">Add Meeting Room</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="progress-steps">
          <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
            <div className="step-indicator"></div>
            <span>Basic Info</span>
          </div>
          <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
            <div className="step-indicator"></div>
            <span>Location</span>
          </div>
          <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
            <div className="step-indicator"></div>
            <span>Amenities</span>
          </div>
          <div className={`step ${currentStep >= 4 ? 'active' : ''}`}>
            <div className="step-indicator"></div>
            <span>Availability</span>
          </div>
          <div className={`step ${currentStep >= 5 ? 'active' : ''}`}>
            <div className="step-indicator"></div>
            <span>Refund Policy</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="meeting-room-form">
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="form-step">
              <h3 className="step-title">Basic Information</h3>
              
              <div className="form-group">
                <label className="form-label">Room Name *</label>
                <input
                  type="text"
                  name="roomName"
                  value={formData.roomName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="e.g., Executive Board Room A"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Room Type *</label>
                <select
                  name="roomType"
                  value={formData.roomType}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  {roomTypes.map(type => (        
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Capacity (People) *</label>
                  <input
                    type="number"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="e.g., 20"
                    min="1"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Price per Hour (₹) *</label>
                  <input
                    type="number"
                    name="pricePerHour"
                    value={formData.pricePerHour}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="e.g., 2500"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Room Visibility *</label>
                <select
                  name="visibility"
                  value={formData.visibility}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="private">Private (only your company can book)</option>
                  <option value="public">Public (all registered companies can book)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Describe your meeting room..."
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Room Images</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="form-file-input"
                />
                <div className="image-preview-grid">
                  {formData.images.map((img, index) => (
                    <div key={index} className="image-preview">
                      <img src={img} alt={`Room ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => removeImage(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Location */}
          {currentStep === 2 && (
            <div className="form-step">
              <h3 className="step-title">Location Details</h3>
              
              <div className="form-group">
                <label className="form-label">Building/Office Name *</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="e.g., Tech Park Tower"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Street address"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">City *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="e.g., Bangalore"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">State *</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="e.g., Karnataka"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Pincode *</label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="e.g., 560001"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Amenities */}
          {currentStep === 3 && (
            <div className="form-step">
              <h3 className="step-title">Amenities & Features</h3>
              
              <div className="amenities-grid">
                {amenitiesList.map(amenity => (
                  <label key={amenity} className="amenity-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.amenities.includes(amenity)}
                      onChange={() => toggleAmenity(amenity)}
                    />
                    <span>{amenity}</span>
                  </label>
                ))}
              </div>

              <div className="form-group">
                <label className="form-label">Rules & Guidelines</label>
                <textarea
                  name="rules"
                  value={formData.rules}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="e.g., No smoking, No food allowed, etc."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Services</label>
                <textarea
                  name="services"
                  value={formData.services}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="e.g., Reception, IT support, catering options, etc."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Surroundings</label>
                <textarea
                  name="surroundings"
                  value={formData.surroundings}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="e.g., Nearby parking, cafes, public transport, etc."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Comments (host notes)</label>
                <textarea
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Any additional notes for guests"
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Policy (cancellation / booking)</label>
                <textarea
                  name="policy"
                  value={formData.policy}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="e.g., Cancellation 24hrs before, payment terms, etc."
                  rows="3"
                />
              </div>
            </div>
          )}

          {/* Step 4: Availability */}
          {currentStep === 4 && (
            <div className="form-step">
              <h3 className="step-title">Availability Schedule</h3>
              
              <div className="availability-list">
                {Object.keys(formData.availability).map(day => (
                  <div key={day} className="availability-row">
                    <label className="day-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.availability[day].available}
                        onChange={(e) => handleAvailabilityChange(day, 'available', e.target.checked)}
                      />
                      <span className="day-name">{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                    </label>
                    
                    {formData.availability[day].available && (
                      <div className="time-inputs">
                        <input
                          type="time"
                          value={formData.availability[day].startTime}
                          onChange={(e) => handleAvailabilityChange(day, 'startTime', e.target.value)}
                          className="time-input"
                        />
                        <span>to</span>
                        <input
                          type="time"
                          value={formData.availability[day].endTime}
                          onChange={(e) => handleAvailabilityChange(day, 'endTime', e.target.value)}
                          className="time-input"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Refund Policy */}
          {currentStep === 5 && (
            <div className="form-step">
              <h3 className="step-title">Refund Policy</h3>
              {/* User Cancellation Policy Section */}
              <div className="refund-section">
                <h4>User Cancellation Policy</h4>
                <div className="form-group">
                  <label className="form-label">Refund Type *</label>
                  <select
                    name="refundType"
                    value={formData.refundType}
                    onChange={handleChange}
                    className="form-input"
                    required
                  >
                    <option value="Fully">Fully Refundable</option>
                    <option value="Partial">Partial Refund</option>
                    <option value="Non-Refundable">Non-Refundable</option>
                  </select>
                </div>
                {(formData.refundType === 'Fully' || formData.refundType === 'Partial') && (
                  <div className="form-group">
                    <label className="form-label">Free Cancellation Before (hours)</label>
                    <input
                      type="number"
                      name="freeCancellationBefore"
                      value={formData.freeCancellationBefore}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="e.g., 24"
                      min="0"
                    />
                  </div>
                )}
                {formData.refundType === 'Partial' && (
                  <div className="form-group">
                    <label className="form-label">Refund Percentage (%)</label>
                    <input
                      type="number"
                      name="refundPercentage"
                      value={formData.refundPercentage}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="e.g., 50"
                      min="0"
                      max="100"
                    />
                  </div>
                )}
              </div>
              {/* Owner Cancellation Rule Section */}
              <div className="refund-section">
                <h4>Owner Cancellation Rule</h4>
                <div className="form-group">
                  <label className="form-label">Owner Cancellation Penalty (₹)</label>
                  <input
                    type="number"
                    name="ownerCancellationPenalty"
                    value={formData.ownerCancellationPenalty}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="e.g., 1000"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <input
                      type="checkbox"
                      name="autoFullRefundToUser"
                      checked={formData.autoFullRefundToUser}
                      onChange={e => setFormData(prev => ({ ...prev, autoFullRefundToUser: e.target.checked }))}
                    />
                    Auto Full Refund to User if Owner Cancels
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Refund Description</label>
                <textarea
                  name="refundDescription"
                  value={formData.refundDescription}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Describe your refund policy..."
                  rows="3"
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="form-actions">
            {currentStep > 1 && (
              <button type="button" onClick={prevStep} className="btn-secondary">
                Previous
              </button>
            )}
            {currentStep < TOTAL_STEPS ? (
              <button
                type="button"
                onClick={nextStep}
                className="btn-primary"
              >
                Next
              </button>
            ) : (
              <button type="submit" className="btn-primary">
                Add Meeting Room
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMeetingRoomForm;
